<template>
  <div class="nd-page account-page">
    <header class="nd-page-header account-page__header">
      <div>
        <h1 class="page-title">Minha Conta</h1>
        <p class="nd-page-kicker">Dados da empresa, assinatura e faturamento.</p>
      </div>
      <v-btn
        v-if="!editingCompany"
        color="primary"
        elevation="0"
        @click="startEditCompany"
      >
        <v-icon left size="18">mdi-pencil-outline</v-icon>
        Editar empresa
      </v-btn>
    </header>

    <div class="account-summary-grid">
      <article class="nd-stat-card">
        <span class="nd-stat-card__label">Empresa</span>
        <strong class="nd-stat-card__value">{{ company.tradeName || company.name || "—" }}</strong>
        <span class="nd-stat-card__hint">{{ company.registration || "CNPJ não informado" }}</span>
      </article>
      <article class="nd-stat-card">
        <span class="nd-stat-card__label">Plano atual</span>
        <strong class="nd-stat-card__value">{{ account.plan ? account.plan.name : "Sem plano" }}</strong>
        <span class="nd-stat-card__hint">{{ account.subscription ? statusLabel(account.subscription.status) : "Escolha um plano" }}</span>
      </article>
      <article class="nd-stat-card">
        <span class="nd-stat-card__label">Próximo vencimento</span>
        <strong class="nd-stat-card__value">{{ account.subscription ? formatDate(account.subscription.nextDueDate) : "—" }}</strong>
        <span class="nd-stat-card__hint">{{ account.subscription ? formatCurrency(account.subscription.value) : "Sem cobrança ativa" }}</span>
      </article>
      <article class="nd-stat-card">
        <span class="nd-stat-card__label">Faturas</span>
        <strong class="nd-stat-card__value">{{ payments.count }}</strong>
        <span class="nd-stat-card__hint">histórico financeiro</span>
      </article>
    </div>

    <section class="account-layout">
      <div class="account-layout__main">
        <v-card class="nd-section-card account-card">
          <div class="account-card__header">
            <div>
              <div class="nd-table-toolbar__title">Dados da empresa</div>
              <div class="nd-table-toolbar__subtitle">Informações cadastrais e endereço da clínica.</div>
            </div>
            <v-btn
              v-if="!editingCompany"
              color="primary"
              outlined
              elevation="0"
              @click="startEditCompany"
            >
              <v-icon left size="18">mdi-pencil-outline</v-icon>
              Editar
            </v-btn>
          </div>

          <div class="account-card__body">
            <div v-if="loadingCompany" class="account-loading">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <div v-else-if="!editingCompany" class="company-profile">
              <div class="company-profile__hero">
                <span class="company-profile__mark">{{ companyInitials }}</span>
                <div>
                  <strong>{{ company.name || "—" }}</strong>
                  <small>{{ company.tradeName || "Nome fantasia não informado" }}</small>
                </div>
              </div>
              <div class="company-profile__grid">
                <div class="account-info">
                  <span>CNPJ</span>
                  <strong>{{ company.registration || "—" }}</strong>
                </div>
                <div class="account-info">
                  <span>Endereço</span>
                  <strong>{{ company.street || "—" }}{{ company.number ? `, ${company.number}` : "" }}</strong>
                </div>
                <div class="account-info">
                  <span>Bairro</span>
                  <strong>{{ company.district || "—" }}</strong>
                </div>
                <div class="account-info">
                  <span>CEP</span>
                  <strong>{{ company.zipcode || "—" }}</strong>
                </div>
                <div class="account-info">
                  <span>Cidade</span>
                  <strong>{{ company.city || "—" }}</strong>
                </div>
                <div class="account-info">
                  <span>Estado</span>
                  <strong>{{ company.state || "—" }}</strong>
                </div>
              </div>
            </div>

            <v-form v-else ref="companyForm" v-model="companyFormValid" class="account-form">
              <div class="account-form__grid">
                <v-text-field label="Razão Social *" v-model="companyEdit.name" :rules="[rules.required]" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="Nome Fantasia *" v-model="companyEdit.tradeName" :rules="[rules.required]" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="CNPJ" :value="company.registration" outlined dense disabled hint="O CNPJ não pode ser alterado" persistent-hint hide-details="auto"></v-text-field>
                <v-text-field label="Rua" v-model="companyEdit.street" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="Número" v-model="companyEdit.number" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="Bairro" v-model="companyEdit.district" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="CEP" v-model="companyEdit.zipCode" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="Cidade" v-model="companyEdit.city" outlined dense hide-details="auto"></v-text-field>
                <v-text-field label="Estado" v-model="companyEdit.state" outlined dense maxlength="2" hide-details="auto"></v-text-field>
              </div>
              <div class="account-form__actions">
                <v-btn text @click="cancelEditCompany">Cancelar</v-btn>
                <v-btn color="primary" elevation="0" :loading="loadingSaveCompany" @click="saveCompany">Salvar alterações</v-btn>
              </div>
            </v-form>
          </div>
        </v-card>

        <v-card class="nd-section-card account-card">
          <div class="account-card__header">
            <div>
              <div class="nd-table-toolbar__title">Planos disponíveis</div>
              <div class="nd-table-toolbar__subtitle">Escolha o plano adequado para a operação da clínica.</div>
            </div>
          </div>
          <div class="account-card__body">
            <div v-if="loadingPlans" class="account-loading">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="!plans.length" class="account-empty account-empty--compact">
              <v-icon color="primary" size="28">mdi-clipboard-text-search-outline</v-icon>
              <strong>Nenhum plano disponível</strong>
              <span>Assim que houver planos cadastrados, eles aparecerão para contratação.</span>
            </div>
            <div v-else class="plan-grid">
              <article
                v-for="plan in plans"
                :key="plan.id"
                class="plan-card"
                :class="{ 'plan-card--active': account.plan && account.plan.id === plan.id }"
              >
                <div class="plan-card__top">
                  <div>
                    <strong>{{ plan.name }}</strong>
                    <small>{{ plan.description || "Plano operacional" }}</small>
                  </div>
                  <span v-if="account.plan && account.plan.id === plan.id" class="nd-status-pill">Atual</span>
                </div>
                <div class="plan-card__price">
                  {{ formatCurrency(plan.value) }}
                  <span>/{{ billingCycleLabel(plan.billingCycle) }}</span>
                </div>
                <div class="plan-card__limits">
                  <span><v-icon size="16">mdi-account-multiple-outline</v-icon> {{ plan.maxUsers || "∞" }} usuários</span>
                  <span><v-icon size="16">mdi-account-heart-outline</v-icon> {{ plan.maxPatients || "∞" }} pacientes</span>
                </div>
                <v-btn
                  color="primary"
                  elevation="0"
                  block
                  :outlined="account.plan && account.plan.id === plan.id"
                  :disabled="account.plan && account.plan.id === plan.id"
                  @click="openSubscribeDialog(plan)"
                >
                  {{ account.plan && account.plan.id === plan.id ? "Plano atual" : (account.subscription ? "Trocar plano" : "Contratar") }}
                </v-btn>
              </article>
            </div>
          </div>
        </v-card>
      </div>

      <aside class="account-layout__side">
        <v-card class="nd-section-card account-card">
          <div class="account-card__header">
            <div>
              <div class="nd-table-toolbar__title">Plano atual</div>
              <div class="nd-table-toolbar__subtitle">Situação comercial da assinatura.</div>
            </div>
          </div>
          <div class="account-card__body">
            <div v-if="loadingAccount" class="account-loading">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="!account.subscription" class="account-empty">
              <v-icon color="primary" size="28">mdi-information-outline</v-icon>
              <strong>Sem assinatura ativa</strong>
              <span>Escolha um plano disponível para iniciar a cobrança.</span>
            </div>
            <div v-else class="current-plan">
              <div class="current-plan__name">
                <strong>{{ account.plan.name }}</strong>
                <v-chip :color="statusColor(account.subscription.status)" dark small>
                  {{ statusLabel(account.subscription.status) }}
                </v-chip>
              </div>
              <div class="account-info">
                <span>Valor</span>
                <strong>{{ formatCurrency(account.subscription.value) }}</strong>
              </div>
              <div class="account-info">
                <span>Próximo vencimento</span>
                <strong>{{ formatDate(account.subscription.nextDueDate) }}</strong>
              </div>
              <div class="account-info" v-if="account.plan">
                <span>Ciclo</span>
                <strong>{{ billingCycleLabel(account.plan.billingCycle) }}</strong>
              </div>
              <div class="account-info" v-if="account.plan">
                <span>Limites</span>
                <strong>{{ account.plan.maxUsers || "∞" }} usuários / {{ account.plan.maxPatients || "∞" }} pacientes</strong>
              </div>
              <v-btn
                v-if="account.subscription.status !== 4"
                color="error"
                outlined
                block
                :loading="loadingCancel"
                @click="confirmCancel"
              >
                Cancelar assinatura
              </v-btn>
            </div>
          </div>
        </v-card>

        <v-card class="nd-section-card account-card">
          <div class="account-card__header">
            <div>
              <div class="nd-table-toolbar__title">Resumo financeiro</div>
              <div class="nd-table-toolbar__subtitle">Últimas movimentações de cobrança.</div>
            </div>
          </div>
          <div class="account-card__body account-quick-list">
            <div class="account-info">
              <span>Total de faturas</span>
              <strong>{{ payments.count }}</strong>
            </div>
            <div class="account-info">
              <span>Último pagamento</span>
              <strong>{{ lastPaymentDate }}</strong>
            </div>
            <div class="account-info">
              <span>Valor do plano</span>
              <strong>{{ account.subscription ? formatCurrency(account.subscription.value) : "—" }}</strong>
            </div>
          </div>
        </v-card>
      </aside>
    </section>

    <v-card class="nd-section-card account-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Histórico de faturas</div>
          <div class="nd-table-toolbar__subtitle">Acompanhe pagamentos, vencimentos e links de cobrança.</div>
        </div>
      </div>
      <v-data-table
        :headers="paymentHeaders"
        :items="payments.data"
        :server-items-length="payments.count"
        :loading="payments.loading"
        @update:page="pageChanged"
        @update:items-per-page="pageSizeChanged"
        :options.sync="payments.options"
        :footer-props="payments.footerOptions"
        no-data-text="Nenhuma fatura encontrada"
        loading-text="Carregando faturas..."
        class="app-table"
        :disable-sort="true"
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="paymentStatusColor(item.status)" dark small>
            {{ paymentStatusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:[`item.billingType`]="{ item }">
          {{ billingTypeLabel(item.billingType) }}
        </template>
        <template v-slot:[`item.value`]="{ item }">
          <div class="nd-entity-cell">
            <span class="nd-entity-mark">R$</span>
            <div>
              <strong>{{ formatCurrency(item.value) }}</strong>
              <small>{{ formatDate(item.dueDate) }}</small>
            </div>
          </div>
        </template>
        <template v-slot:[`item.paymentDate`]="{ item }">
          {{ item.paymentDate ? formatDate(item.paymentDate) : "—" }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="row-actions">
            <v-tooltip bottom v-if="item.invoiceUrl">
              <template #activator="{ on }">
                <v-btn icon small :href="item.invoiceUrl" target="_blank" v-on="on">
                  <v-icon size="19">mdi-receipt-text-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver fatura</span>
            </v-tooltip>
            <v-tooltip bottom v-if="item.bankSlipUrl">
              <template #activator="{ on }">
                <v-btn icon small :href="item.bankSlipUrl" target="_blank" v-on="on">
                  <v-icon size="19">mdi-barcode</v-icon>
                </v-btn>
              </template>
              <span>Ver boleto</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog Contratar / Trocar Plano -->
    <v-dialog v-model="subscribeDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>
          {{ account.subscription ? 'Trocar para ' : 'Contratar ' }} {{ selectedPlan && selectedPlan.name }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="account.subscription" type="warning" outlined class="mb-4">
            Seu plano atual será cancelado e substituído pelo novo plano.
          </v-alert>
          <v-form ref="subscribeForm" v-model="subscribeFormValid">
            <v-select
              label="Forma de Pagamento"
              v-model="subscribeData.billingType"
              :items="billingTypeOptions"
              :rules="[rules.required]"
              outlined
              dense
             hide-details="auto"></v-select>
            <v-text-field
              label="CPF / CNPJ"
              v-model="subscribeData.cpfCnpj"
              :rules="[rules.required]"
              outlined
              dense
              hint="Informe o CPF ou CNPJ do titular"
              persistent-hint
             hide-details="auto"></v-text-field>
            <v-text-field
              label="E-mail"
              v-model="subscribeData.email"
              outlined
              dense
             hide-details="auto"></v-text-field>
            <v-text-field
              label="Telefone"
              v-model="subscribeData.phone"
              outlined
              dense
             hide-details="auto"></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="subscribeDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="loadingSubscribe" @click="confirmSubscribe">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loadingAccount && !account.subscription">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AccountManagement",

  data: () => ({
    loadingAccount: false,
    loadingPlans: false,
    loadingCancel: false,
    loadingSubscribe: false,
    loadingCompany: false,
    loadingSaveCompany: false,
    editingCompany: false,
    companyFormValid: false,

    company: {
      id: null,
      name: '',
      tradeName: '',
      registration: '',
      street: '',
      district: '',
      number: '',
      city: '',
      state: '',
      zipcode: '',
    },

    companyEdit: {
      name: '',
      tradeName: '',
      street: '',
      district: '',
      number: '',
      city: '',
      state: '',
      zipCode: '',
    },

    account: {
      subscription: null,
      plan: null,
    },

    plans: [],

    payments: {
      data: [],
      count: 0,
      loading: false,
      options: { page: 1, itemsPerPage: 10 },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30],
        pageText: "{0}-{1} de {2}",
      },
    },

    paymentHeaders: [
      { text: "Status", value: "status", sortable: false },
      { text: "Tipo", value: "billingType", sortable: false },
      { text: "Valor", value: "value", sortable: false },
      { text: "Vencimento", value: "dueDate", sortable: false },
      { text: "Pagamento", value: "paymentDate", sortable: false },
      { text: "Ações", value: "actions", sortable: false },
    ],

    subscribeDialog: false,
    subscribeFormValid: false,
    selectedPlan: null,
    subscribeData: {
      billingType: null,
      cpfCnpj: "",
      email: "",
      phone: "",
    },

    billingTypeOptions: [
      { text: "PIX", value: "PIX" },
      { text: "Boleto", value: "BOLETO" },
      { text: "Cartão de Crédito", value: "CREDIT_CARD" },
    ],

    rules: {
      required: (v) => !!v || "Campo obrigatório.",
    },
  }),

  async created() {
    await Promise.all([this.loadAccount(), this.loadPlans(), this.loadCompany()]);
    await this.loadPayments();
  },

  computed: {
    companyInitials() {
      const name = this.company.tradeName || this.company.name || "ND";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },

    lastPaymentDate() {
      const paid = this.payments.data.find((item) => item.paymentDate);
      return paid ? this.formatDate(paid.paymentDate) : "—";
    },
  },

  methods: {
    async loadCompany() {
      this.loadingCompany = true;
      try {
        const res = await axios.get("/account/company");
        this.company = res.data.data;
      } catch {
        this.$dialog.notify.error("Erro ao carregar dados da empresa.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingCompany = false;
      }
    },

    startEditCompany() {
      this.companyEdit = {
        name: this.company.name || '',
        tradeName: this.company.tradeName || '',
        street: this.company.street || '',
        district: this.company.district || '',
        number: this.company.number || '',
        city: this.company.city || '',
        state: this.company.state || '',
        zipCode: this.company.zipcode || '',
      };
      this.editingCompany = true;
    },

    cancelEditCompany() {
      this.editingCompany = false;
    },

    async saveCompany() {
      if (!this.$refs.companyForm.validate()) return;
      this.loadingSaveCompany = true;
      try {
        const res = await axios.patch("/account/company", this.companyEdit);
        this.company = res.data.data;
        this.editingCompany = false;
        this.$dialog.notify.success("Dados da empresa atualizados.", { position: "top-right", timeout: 3000 });
      } catch (error) {
        const msg = error?.data?.data?.message || "Erro ao salvar dados da empresa.";
        this.$dialog.notify.error(msg, { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingSaveCompany = false;
      }
    },

    async loadAccount() {
      this.loadingAccount = true;
      try {
        const res = await axios.get("/account");
        this.account = res.data.data;
      } catch {
        this.$dialog.notify.error("Erro ao carregar dados da conta.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingAccount = false;
      }
    },

    async loadPlans() {
      this.loadingPlans = true;
      try {
        const res = await axios.get("/account/plans");
        this.plans = res.data.data;
      } catch {
        this.$dialog.notify.error("Erro ao carregar planos.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingPlans = false;
      }
    },

    async loadPayments() {
      this.payments.loading = true;
      try {
        const start = (this.payments.options.page - 1) * this.payments.options.itemsPerPage;
        const res = await axios.get("/account/payments", {
          params: { start, limit: this.payments.options.itemsPerPage },
        });
        this.payments.data = res.data.data || [];
        this.payments.count = res.data.count || 0;
      } catch {
        // sem assinatura = sem faturas, silencioso
      } finally {
        this.payments.loading = false;
      }
    },

    pageChanged(page) {
      this.payments.options.page = page;
      this.loadPayments();
    },

    pageSizeChanged(size) {
      this.payments.options.itemsPerPage = size;
      this.loadPayments();
    },

    openSubscribeDialog(plan) {
      this.selectedPlan = plan;
      this.subscribeData = { billingType: null, cpfCnpj: "", email: "", phone: "" };
      this.subscribeDialog = true;
    },

    async confirmSubscribe() {
      if (!this.$refs.subscribeForm.validate()) return;

      this.loadingSubscribe = true;
      try {
        if (this.account.subscription) {
          await axios.delete("/account/cancel");
        }

        await axios.post("/account/subscribe", {
          planId: this.selectedPlan.id,
          billingType: this.subscribeData.billingType,
          cpfCnpj: this.subscribeData.cpfCnpj || undefined,
          email: this.subscribeData.email || undefined,
          phone: this.subscribeData.phone || undefined,
        });

        this.$dialog.notify.success("Assinatura realizada com sucesso!", { position: "top-right", timeout: 3000 });
        this.subscribeDialog = false;
        await this.loadAccount();
        await this.loadPayments();
      } catch (error) {
        const msg = error?.response?.data?.data?.message || "Erro ao processar assinatura. Tente novamente.";
        this.$dialog.notify.error(msg, { position: "top-right", timeout: 6000 });
      } finally {
        this.loadingSubscribe = false;
      }
    },

    async confirmCancel() {
      const proceed = await this.$dialog.confirm({
        text: "Deseja realmente cancelar sua assinatura? O acesso continuará até o fim do período pago.",
        title: "Cancelar Assinatura",
        actions: {
          false: "Voltar",
          true: { text: "Cancelar Assinatura", color: "error" },
        },
      });

      if (!proceed) return;

      this.loadingCancel = true;
      try {
        await axios.delete("/account/cancel");
        this.$dialog.notify.info("Assinatura cancelada.", { position: "top-right", timeout: 3000 });
        await this.loadAccount();
        await this.loadPayments();
      } catch (error) {
        const msg = error?.response?.data?.data?.message || "Erro ao cancelar assinatura.";
        this.$dialog.notify.error(msg, { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingCancel = false;
      }
    },

    formatCurrency(value) {
      if (value == null) return "R$ 0,00";
      return `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
    },

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },

    billingCycleLabel(cycle) {
      return { 1: "Mês", 2: "Trimestre", 3: "Semestre", 4: "Ano" }[cycle] || "—";
    },

    billingTypeLabel(type) {
      return { 1: "Boleto", 2: "Cartão de Crédito", 3: "PIX", 4: "Débito" }[type] || "—";
    },

    statusLabel(status) {
      return { 1: "Ativo", 2: "Inativo", 3: "Inadimplente", 4: "Cancelado", 5: "Pendente" }[status] || "—";
    },

    statusColor(status) {
      return { 1: "green", 2: "grey", 3: "orange", 4: "red", 5: "blue" }[status] || "grey";
    },

    paymentStatusLabel(status) {
      return { 1: "Pendente", 2: "Confirmado", 3: "Recebido", 4: "Vencido", 5: "Estornado", 6: "Cancelado" }[status] || "—";
    },

    paymentStatusColor(status) {
      return { 1: "orange", 2: "green", 3: "green darken-2", 4: "red", 5: "purple", 6: "grey" }[status] || "grey";
    },
  },
};
</script>
